import React, { useState, useEffect, useRef } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Alert, AlertDescription } from "../ui/alert";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "../ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Upload,
  X,
  Palette,
  Type,
  Globe,
  Mail,
  Phone,
  MapPin,
  Twitter,
  Linkedin,
  Facebook,
  Instagram,
  Youtube,
  Github,
  Eye,
  Save,
  Loader2,
  ImageIcon,
  AlertCircle,
} from "lucide-react";
import { toast } from "../../hooks/use-toast";
import BrandingService from "../../services/brandingService";
import {
  OrganizationBranding,
  CreateBrandingData,
  Organization,
} from "../../types/organization";

// Form validation schema
const brandingSchema = z.object({
  brand_title: z.string().optional(),
  tagline: z.string().optional(),
  primary_color: z
    .string()
    .optional()
    .refine(
      (val) => !val || BrandingService.isValidHexColor(val),
      "Must be a valid hex color (e.g., #FF5733)"
    ),
  secondary_color: z
    .string()
    .optional()
    .refine(
      (val) => !val || BrandingService.isValidHexColor(val),
      "Must be a valid hex color"
    ),
  accent_color: z
    .string()
    .optional()
    .refine(
      (val) => !val || BrandingService.isValidHexColor(val),
      "Must be a valid hex color"
    ),
  font_color: z
    .string()
    .optional()
    .refine(
      (val) => !val || BrandingService.isValidHexColor(val),
      "Must be a valid hex color"
    ),
  font_family: z.string().optional(),
  logo_position: z.enum(["left", "center", "right"]).optional(),
  theme_mode: z.enum(["light", "dark", "auto"]).optional(),
  website_url: z
    .string()
    .optional()
    .refine(
      (val) => !val || BrandingService.isValidUrl(val),
      "Must be a valid URL starting with http:// or https://"
    ),
  contact_email: z
    .string()
    .optional()
    .refine(
      (val) => !val || BrandingService.isValidEmail(val),
      "Must be a valid email address"
    ),
  phone_number: z.string().optional(),
  address_street: z.string().optional(),
  address_city: z.string().optional(),
  address_state: z.string().optional(),
  address_postal_code: z.string().optional(),
  address_country: z.string().optional(),
  social_twitter: z.string().optional(),
  social_linkedin: z.string().optional(),
  social_facebook: z.string().optional(),
  social_instagram: z.string().optional(),
  social_youtube: z.string().optional(),
  social_github: z.string().optional(),
});

type BrandingFormValues = z.infer<typeof brandingSchema>;

interface BrandingEditorProps {
  organization: Organization;
  onSuccess?: () => void;
}

export function BrandingEditor({
  organization,
  onSuccess,
}: BrandingEditorProps) {
  const [branding, setBranding] = useState<OrganizationBranding | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"visual" | "contact" | "social">(
    "visual"
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<BrandingFormValues>({
    resolver: zodResolver(brandingSchema),
    defaultValues: {},
  });

  // Load existing branding
  useEffect(() => {
    loadBranding();
  }, [organization.id]);

  const loadBranding = async () => {
    try {
      setLoading(true);
      const brandingData = await BrandingService.getOrganizationBranding(
        organization.id
      );

      if (brandingData) {
        setBranding(brandingData);
        setLogoPreview(brandingData.logo_url
          ? BrandingService.getLogoUrlWithCacheBusting(brandingData.logo_url)
          : null
        );

        // Populate form with existing data
        form.reset({
          brand_title: brandingData.brand_title || "",
          tagline: brandingData.tagline || "",
          primary_color: brandingData.primary_color || "",
          secondary_color: brandingData.secondary_color || "",
          accent_color: brandingData.accent_color || "",
          font_color: brandingData.font_color || "",
          font_family: brandingData.font_family || "Inter",
          logo_position: brandingData.logo_position || "left",
          theme_mode: brandingData.theme_mode || "light",
          website_url: brandingData.website_url || "",
          contact_email: brandingData.contact_email || "",
          phone_number: brandingData.phone_number || "",
          address_street: brandingData.address?.street || "",
          address_city: brandingData.address?.city || "",
          address_state: brandingData.address?.state || "",
          address_postal_code: brandingData.address?.postal_code || "",
          address_country: brandingData.address?.country || "",
          social_twitter: brandingData.social_links?.twitter || "",
          social_linkedin: brandingData.social_links?.linkedin || "",
          social_facebook: brandingData.social_links?.facebook || "",
          social_instagram: brandingData.social_links?.instagram || "",
          social_youtube: brandingData.social_links?.youtube || "",
          social_github: brandingData.social_links?.github || "",
        });
      }
    } catch (error) {
      console.error("Error loading branding:", error);
      toast({
        title: "Error",
        description: "Failed to load branding data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      console.log(`[BrandingEditor] 📤 Starting logo upload for organization: ${organization.id}`);

      // Create preview
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);

      // Upload to storage
      console.log(`[BrandingEditor] ☁️ Uploading file to storage...`);
      const result = await BrandingService.uploadOrganizationLogo(
        organization.id,
        file
      );

      if (result.success && result.url) {
        console.log(`[BrandingEditor] ✅ File uploaded successfully: ${result.url}`);

        // Update branding with new logo URL
        console.log(`[BrandingEditor] 💾 Updating branding database with logo URL...`);
        await BrandingService.upsertOrganizationBranding(organization.id, {
          logo_url: result.url,
        });

        setLogoPreview(BrandingService.getLogoUrlWithCacheBusting(result.url));

        toast({
          title: "Success",
          description: "Logo uploaded successfully",
        });

        console.log(`[BrandingEditor] ✅ Logo upload process completed successfully`);

        // Reload branding data
        await loadBranding();
      } else {
        console.error(`[BrandingEditor] ❌ File upload failed:`, result.error);
        throw new Error(result.error || "Upload failed");
      }
    } catch (error) {
      console.error("[BrandingEditor] ❌ Logo upload error:", error);

      // Provide more specific error message
      let errorMessage = "Failed to upload logo";
      if (error instanceof Error) {
        if (error.message.includes("permission denied")) {
          errorMessage = "Permission denied. You may need admin access to update branding.";
        } else if (error.message.includes("storage")) {
          errorMessage = "Failed to upload file to storage. Please try again.";
        } else {
          errorMessage = error.message;
        }
      }

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      // Reset preview on error
      setLogoPreview(branding?.logo_url || null);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemoveLogo = async () => {
    try {
      setUploading(true);

      // Delete from storage
      await BrandingService.deleteLogoFromStorage(organization.id);

      // Update branding to remove logo URL
      if (branding) {
        await BrandingService.updateOrganizationBranding({
          id: branding.id,
          logo_url: null,
        });
      }

      setLogoPreview(null);

      toast({
        title: "Success",
        description: "Logo removed successfully",
      });

      await loadBranding();
    } catch (error) {
      console.error("Error removing logo:", error);
      toast({
        title: "Error",
        description: "Failed to remove logo",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (values: BrandingFormValues) => {
    try {
      setSaving(true);

      // Prepare branding data
      const brandingData: Partial<CreateBrandingData> = {
        brand_title: values.brand_title || undefined,
        tagline: values.tagline || undefined,
        primary_color: values.primary_color || undefined,
        secondary_color: values.secondary_color || undefined,
        accent_color: values.accent_color || undefined,
        font_color: values.font_color || undefined,
        font_family: values.font_family || undefined,
        logo_position: values.logo_position || undefined,
        theme_mode: values.theme_mode || undefined,
        website_url: values.website_url || undefined,
        contact_email: values.contact_email || undefined,
        phone_number: values.phone_number || undefined,
        address: {
          street: values.address_street || undefined,
          city: values.address_city || undefined,
          state: values.address_state || undefined,
          postal_code: values.address_postal_code || undefined,
          country: values.address_country || undefined,
        },
        social_links: {
          twitter: values.social_twitter || undefined,
          linkedin: values.social_linkedin || undefined,
          facebook: values.social_facebook || undefined,
          instagram: values.social_instagram || undefined,
          youtube: values.social_youtube || undefined,
          github: values.social_github || undefined,
        },
      };

      // Clean up empty objects
      if (Object.values(brandingData.address || {}).every((v) => !v)) {
        brandingData.address = undefined;
      }
      if (Object.values(brandingData.social_links || {}).every((v) => !v)) {
        brandingData.social_links = undefined;
      }

      await BrandingService.upsertOrganizationBranding(
        organization.id,
        brandingData
      );

      toast({
        title: "Success",
        description: "Branding updated successfully",
      });

      await loadBranding();
      onSuccess?.();
    } catch (error) {
      console.error("Error saving branding:", error);
      toast({
        title: "Error",
        description: "Failed to save branding",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const ColorInput = ({
    value,
    onChange,
    label,
  }: {
    value: string;
    onChange: (value: string) => void;
    label: string;
  }) => (
    <div className="flex items-center gap-2">
      <div className="relative">
        <input
          type="color"
          value={value || "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-10 rounded border cursor-pointer"
        />
      </div>
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#000000"
        className="flex-1"
      />
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span className="ml-2">Loading branding...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Organization Branding</h3>
          <p className="text-sm text-muted-foreground">
            Customize your organization's visual identity and contact
            information
          </p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Tabs */}
          <div className="flex space-x-1 bg-muted p-1 rounded-lg">
            <button
              type="button"
              onClick={() => setActiveTab("visual")}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === "visual"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Palette className="h-4 w-4" />
              Visual Branding
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("contact")}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === "contact"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Mail className="h-4 w-4" />
              Contact Info
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("social")}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === "social"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Twitter className="h-4 w-4" />
              Social Media
            </button>
          </div>

          {/* Visual Branding Tab */}
          {activeTab === "visual" && (
            <div className="space-y-6">
              {/* Logo Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ImageIcon className="h-5 w-5" />
                    Logo
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      {logoPreview ? (
                        <div className="relative">
                          <img
                            src={logoPreview}
                            alt="Organization Logo"
                            className="w-32 h-32 object-contain border rounded-lg dark:bg-white/10 bg-gray-50"
                            onError={() => setLogoPreview(null)}
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0"
                            onClick={handleRemoveLogo}
                            disabled={uploading}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <div className="w-32 h-32 border-2 border-dashed border-muted-foreground/25 rounded-lg flex items-center justify-center">
                          <div className="text-center">
                            <ImageIcon className="h-8 w-8 mx-auto text-muted-foreground/50" />
                            <p className="text-sm text-muted-foreground mt-1">
                              No logo
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex-1 space-y-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="w-full"
                      >
                        {uploading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4 mr-2" />
                            Upload Logo
                          </>
                        )}
                      </Button>
                      <p className="text-xs text-muted-foreground">
                        PNG, JPG, or SVG. Max 5MB.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="logo_position"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Logo Position</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select position" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="left">Left</SelectItem>
                              <SelectItem value="center">Center</SelectItem>
                              <SelectItem value="right">Right</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="theme_mode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Theme Mode</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select theme" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="light">Light</SelectItem>
                              <SelectItem value="dark">Dark</SelectItem>
                              <SelectItem value="auto">Auto</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Text Branding */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Type className="h-5 w-5" />
                    Text Branding
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="brand_title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Brand Title</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Your Organization Name"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Custom title to display instead of organization name
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="tagline"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tagline</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Your brand tagline or slogan"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="font_family"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Font Family</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select font" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {BrandingService.getAvailableFonts().map((font) => (
                              <SelectItem key={font.value} value={font.value}>
                                {font.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Colors */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Palette className="h-5 w-5" />
                    Brand Colors
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="primary_color"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Primary Color</FormLabel>
                        <FormControl>
                          <ColorInput
                            value={field.value || ""}
                            onChange={field.onChange}
                            label="Primary"
                          />
                        </FormControl>
                        <FormDescription>Main brand color</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="secondary_color"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Secondary Color</FormLabel>
                        <FormControl>
                          <ColorInput
                            value={field.value || ""}
                            onChange={field.onChange}
                            label="Secondary"
                          />
                        </FormControl>
                        <FormDescription>Supporting color</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="font_color"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Font Color</FormLabel>
                        <FormControl>
                          <ColorInput
                            value={field.value || ""}
                            onChange={field.onChange}
                            label="Font"
                          />
                        </FormControl>
                        <FormDescription>Default text color</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </div>
          )}

          {/* Contact Info Tab */}
          {activeTab === "contact" && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    Contact Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="website_url"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Website URL</FormLabel>
                        <FormControl>
                          <Input placeholder="https://example.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="contact_email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Contact Email</FormLabel>
                        <FormControl>
                          <Input placeholder="contact@example.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone Number</FormLabel>
                        <FormControl>
                          <Input placeholder="+1 (555) 123-4567" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    Address
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="address_street"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Street Address</FormLabel>
                        <FormControl>
                          <Input placeholder="123 Main Street" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="address_city"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>City</FormLabel>
                          <FormControl>
                            <Input placeholder="New York" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="address_state"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>State/Province</FormLabel>
                          <FormControl>
                            <Input placeholder="NY" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="address_postal_code"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Postal Code</FormLabel>
                          <FormControl>
                            <Input placeholder="10001" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="address_country"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Country</FormLabel>
                          <FormControl>
                            <Input placeholder="United States" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Social Media Tab */}
          {activeTab === "social" && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Twitter className="h-5 w-5" />
                  Social Media Links
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="social_twitter"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Twitter className="h-4 w-4" />
                        Twitter
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="https://twitter.com/yourcompany"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="social_linkedin"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Linkedin className="h-4 w-4" />
                        LinkedIn
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="https://linkedin.com/company/yourcompany"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="social_facebook"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Facebook className="h-4 w-4" />
                        Facebook
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="https://facebook.com/yourcompany"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="social_instagram"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Instagram className="h-4 w-4" />
                        Instagram
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="https://instagram.com/yourcompany"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="social_youtube"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Youtube className="h-4 w-4" />
                        YouTube
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="https://youtube.com/c/yourcompany"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="social_github"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Github className="h-4 w-4" />
                        GitHub
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="https://github.com/yourcompany"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          )}

          {/* Save Button */}
          <div className="flex justify-end space-x-2">
            <Button type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Branding
                </>
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
 